import React from 'react';
import { DeckContext, DeckImage, DeckItem, Slide, SlideComputed, SlideMode } from '../types';

export interface SlideCtx {
    slide: Slide;
    context: DeckContext;
    computed: SlideComputed;
    mode: SlideMode;
    edit: boolean;
    /** Changes whenever the slide's content does; Fit boxes re-measure on it. */
    sig: string;
    onFieldChange?: (key: string, value: unknown) => void;
    onSelectField?: (key: string | null) => void;
    renderImageSlot?: (key: string, img: DeckImage | null) => React.ReactNode;
    onAction?: (action: 'cta', url: string) => void;
}

export const SlideContext = React.createContext<SlideCtx | null>(null);

// #region useSlide
export function useSlide(): SlideCtx {
    const ctx = React.useContext(SlideContext);
    if (!ctx) throw new Error('useSlide outside SlideView');
    return ctx;
}

// #region field readers
export const str = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
export const num = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() && Number.isFinite(Number(v)) ? Number(v) : fallback);
export const bool = (v: unknown, fallback = false): boolean => (typeof v === 'boolean' ? v : fallback);

export function img(v: unknown): DeckImage | null {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
    const o = v as Partial<DeckImage>;
    return typeof o.url === 'string' && /^https:\/\//.test(o.url) ? { url: o.url, alt: typeof o.alt === 'string' ? o.alt : '', asset_id: o.asset_id ?? null } : null;
}

export function items(v: unknown): DeckItem[] {
    return Array.isArray(v) ? v.filter((x): x is DeckItem => !!x && typeof x === 'object' && !Array.isArray(x)) : [];
}

export function rows(v: unknown): string[][] {
    return Array.isArray(v) ? v.filter((r): r is unknown[] => Array.isArray(r)).map((r) => r.map((c) => str(c))) : [];
}
